// Defensive reader over the products.price jsonb column. The full pricing
// engine in @travel-planner/shared/pricing computes display totals via
// `calculatePricing(input)` from the SingleProductPrice/AccommodationPrice
// shapes — for a list cell or row we only need a single numeric value to
// render, so we walk the common shapes shallowly and fall back to null when
// nothing matches.
//
// Lives in the shared pricing module so the products web entity and the
// itineraries web widgets (item rows, subitem rows) read one copy. Previously
// duplicated in apps/products/web/src/entities/product/lib/price.ts.

const isNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

// A per-person price lists one row per traveller type, in whatever order they
// were entered. The adult row is the reference: every proposal has adults, not
// every one has children (XT-789).
const travellerRowPrice = (
  travellers: unknown,
  travellerIds: ReadonlySet<string>
): number | null => {
  if (!Array.isArray(travellers)) return null;
  for (const entry of travellers) {
    const row = entry as { travellerId?: unknown; price?: unknown } | null;
    if (
      typeof row?.travellerId === 'string' &&
      travellerIds.has(row.travellerId) &&
      isNumber(row.price)
    ) {
      return row.price;
    }
  }
  return null;
};

/**
 * The per-person price's row for one of `travellerIds` (e.g. the org's child
 * types), or null when the price lists none of them.
 */
export const extractTravellerTypePrice = (
  price: unknown,
  travellerIds: ReadonlySet<string> | undefined
): number | null => {
  if (!travellerIds || travellerIds.size === 0) return null;
  if (price == null || typeof price !== 'object') return null;
  return travellerRowPrice(
    (price as Record<string, unknown>).travellers,
    travellerIds
  );
};

/**
 * `adultTravellerIds` — the organization's adult traveller types. When given,
 * a per-person price shows its adult row; without it (or with no adult row)
 * the first row found is shown, as before.
 */
export const extractDisplayPrice = (
  price: unknown,
  adultTravellerIds?: ReadonlySet<string>
): number | null => {
  if (price == null) return null;
  if (isNumber(price)) return price;
  if (typeof price !== 'object') return null;
  const obj = price as Record<string, unknown>;
  // Single product shape: { price, cost, netCost, markup }
  if (isNumber(obj.price)) return obj.price;
  if (isNumber(obj.netCost)) return obj.netCost;
  if (isNumber(obj.cost)) return obj.cost;
  if (adultTravellerIds && adultTravellerIds.size > 0) {
    const adult = travellerRowPrice(obj.travellers, adultTravellerIds);
    if (adult != null) return adult;
  }
  // Rooms / travellers nested shapes — pick the first nested numeric price.
  for (const value of Object.values(obj)) {
    if (Array.isArray(value)) {
      for (const entry of value) {
        const inner = extractDisplayPrice(entry);
        if (inner != null) return inner;
      }
    } else if (value && typeof value === 'object') {
      const inner = extractDisplayPrice(value);
      if (inner != null) return inner;
    }
  }
  return null;
};
